// The currencies a donor can give in, and everything that differs per currency.
//
// Every fact here was read off the live IsraelGives donation page for Standing
// Together's amuta (515495067) rather than guessed — see the notes on each
// field. Their page is the authority; if IsraelGives changes an account setting
// (which currencies clear, which support recurring) this file has to follow.

// `id` is IsraelGives' own currency id — the value of the #ddlDonationCurrency
// <option> we drive by POSTing `currency`. Their API doc ("IsraelGives API
// Collection", the currency-id table) is explicit that the currency "must be
// passed as an id, not as text", which is what `buildIgPayment` sends.
//
// Every id below was checked on 2026-08-11 against BOTH that table and the
// account's own dropdown. The two lists disagree in both directions, and where
// they do the dropdown wins — see the note above the second block of additions.
//
// `countryId` is the country their page pairs the currency with by default
// (their `CurrencyWithCountry` table); AUD isn't in that table, so Australia's
// own id from their country list is used. It's only a DEFAULT — the donor picks
// their country on the non-ILS form and that choice wins.
//
// `recurring` mirrors their per-account `currencyEngines` config: card
// (Stripe, engine 5) recurring is enabled ONLY for EUR and AUD on this amuta
// (plus ILS, which is never gated). It is DOCUMENTATION ONLY — nothing reads
// it. The monthly tab and the membership page offer every currency in
// CURRENCY_ORDER, exactly like the one-time tab; see the DECIDED note below.
// The column stays because it is the only written record of what the account
// declares, and putting the restriction back means reading this line rather
// than rediscovering the flag in a browser session:
//
// HOW TO RE-CHECK THAT (it took a browser session to rediscover once already).
// Both the data and the gate are declared in INLINE scripts on their form —
// there is no .js file to read and nothing about it in their API docs, because
// the array is server-rendered per amuta from the AmutaGovId query param:
//
//   https://secureddonation.com/en/pay/makedonation?MakeDonation=1&AmutaGovId=515495067
//
// In that page's console (`var currencyEngines` is in inline script #3,
// `function IsEngineEnabledForCurrency` in #22):
//
//   currencyEngines.map(e => e.CurrencyId).filter(c => IsEngineEnabledForCurrency(c, 5, true))
//
// Last run 2026-08-11: [1, 5, 6] — ILS, EUR, AUD, exactly the three carrying
// `recurring: true`. Engine 5 is the card engine; engine 0 is the unset-engine
// sentinel and answers true for nearly everything, so probing it proves
// nothing. ILS is
// never gated at all (true on every engine) — the raw currencyEngines row for
// it actually carries IsRecurringEnabled:false, and only the function tells you
// the local currency is waved through. Read the array without the function and
// you will get ILS wrong.
//
// CAVEAT, same date: their own form does NOT enforce this up front. Deep-link
// it to the card step with a supposedly-refused pair
// (currency=2&freq=2&sum=10&step=2) and it renders the monthly USD card form
// without complaint. So either the block is server-side at authorization or
// the flag governs something narrower. The only test that separates those is a
// real monthly USD charge.
//
// DECIDED 2026-08-11: offer monthly in EVERY currency regardless. Narrowing
// the monthly picker to three currencies turned away every would-be monthly
// dollar, pound and franc donor at the door — a certain loss — to avoid a
// refusal their own form gives no sign of applying. The risk that was traded
// for it is real and worth naming: if the flag does bite, it bites AFTER the
// card number is in. To put the restriction back, gate the pickers on
// `recurring` again (`recurringOnly` on CurrencyPicker in git history).
//
// `addressRequired` is their `AdressIsMandatory()`: address is compulsory for
// CAD/GBP/EUR. It is documentation only — `needsAddress` ignores it and
// collects the address on every foreign currency (see below) — which is why the
// currencies added on 2026-08-11 carry `false` without it having been
// established either way. Their function reads page state and takes no
// arguments, so it can't be probed per-currency the way the recurring gate can.
//
// `feeFixed` is the fixed half of the "cover the transaction fee" sum, ~₪1.9
// converted at a round rate. It is an ESTIMATE of a voluntary top-up, not a
// billed figure, so a rough constant is deliberate: making it exact would mean
// blocking the amount widget on a live FX call for a couple of cents.
// `label` carries the currency's NAME only — no symbol. The picker renders the
// symbol itself as a leading glyph (see CurrencyPicker), so repeating it here
// would print "₪ שקל ₪".
export const CURRENCIES = {
  ILS: {
    code: 'ILS', id: '1', countryId: '104', symbol: '₪', symbolFirst: false,
    recurring: true, addressRequired: false, feeFixed: 1.9, min: 5,
    label: { he: 'שקל', ar: 'شيكل' },
  },
  USD: {
    code: 'USD', id: '2', countryId: '223', symbol: '$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 0.5, min: 5,
    label: { he: 'דולר', ar: 'دولار' },
  },
  EUR: {
    code: 'EUR', id: '5', countryId: '73', symbol: '€', symbolFirst: true,
    recurring: true, addressRequired: true, feeFixed: 0.5, min: 5,
    label: { he: 'אירו', ar: 'يورو' },
  },
  GBP: {
    code: 'GBP', id: '4', countryId: '222', symbol: '£', symbolFirst: true,
    recurring: false, addressRequired: true, feeFixed: 0.4, min: 5,
    label: { he: 'לירה שטרלינג', ar: 'جنيه إسترليني' },
  },
  CAD: {
    code: 'CAD', id: '3', countryId: '38', symbol: 'C$', symbolFirst: true,
    recurring: false, addressRequired: true, feeFixed: 0.7, min: 5,
    label: { he: 'דולר קנדי', ar: 'دولار كندي' },
  },
  AUD: {
    code: 'AUD', id: '6', countryId: '13', symbol: 'A$', symbolFirst: true,
    recurring: true, addressRequired: false, feeFixed: 0.8, min: 5,
    label: { he: 'דולר אוסטרלי', ar: 'دولار أسترالي' },
  },

  // Added 2026-08-11. One-time only — engine 5 takes a one-off charge in all 28
  // currencies the account carries, and a recurring one in only the three
  // above. `countryId` for each was read off their own #ddlcountry list on the
  // page linked at the top of this file (Switzerland 204 matched the entry that
  // was already here, which is the check that the others are right too).
  //
  // `min` is a round number worth roughly what the ₪15-ish USD minimum is,
  // rather than a flat 5 — 5 rand is 92 agorot, which is not a donation floor.
  // `feeFixed` is ₪1.9 in each currency at the 2026-08-11 rate, rounded.
  CHF: {
    code: 'CHF', id: '17', countryId: '204', symbol: 'CHF', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 0.5, min: 5,
    label: { he: 'פרנק שוויצרי', ar: 'فرنك سويسري' },
  },
  SEK: {
    code: 'SEK', id: '16', countryId: '203', symbol: 'kr', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 6, min: 50,
    label: { he: 'כתר שוודי', ar: 'كرونة سويدية' },
  },
  NOK: {
    code: 'NOK', id: '12', countryId: '160', symbol: 'kr', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 6, min: 50,
    label: { he: 'כתר נורווגי', ar: 'كرونة نرويجية' },
  },
  DKK: {
    code: 'DKK', id: '8', countryId: '57', symbol: 'kr', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 4, min: 35,
    label: { he: 'כתר דני', ar: 'كرونة دنماركية' },
  },
  NZD: {
    code: 'NZD', id: '13', countryId: '153', symbol: 'NZ$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 1.1, min: 10,
    label: { he: 'דולר ניו זילנדי', ar: 'دولار نيوزيلندي' },
  },
  ZAR: {
    code: 'ZAR', id: '33', countryId: '193', symbol: 'R', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 10, min: 80,
    label: { he: 'ראנד דרום אפריקאי', ar: 'راند جنوب أفريقي' },
  },

  // Added 2026-08-11, second pass — the account's dropdown read in full and
  // diffed against the API doc's id table. Same rules as the block above:
  // one-time only, `countryId` from their own #ddlcountry list, `feeFixed` is
  // ₪1.9 and `min` is ~₪15 in each currency at that day's rate, both rounded to
  // a figure a donor would plausibly type rather than to the exact conversion.
  //
  // WHICH OF THE ACCOUNT'S 28 ARE HERE, AND WHY THE REST ARE NOT.
  // This file now carries 21. The 7 left out — RUB, AED, MAD, SAR, ARS, UAH and
  // CLP — are exactly the ones the ECB publishes no rate for. That is a hard
  // gate rather than a preference: a currency absent from the FX feed records a
  // null shekel value and drops out of every campaign total (server/fx.js), so
  // any future addition must appear in FX_SYMBOLS there or it must not go in
  // here. fx.test.js pins the two lists together and fails if this one runs
  // ahead, so the gate is enforced rather than merely documented.
  //
  // Two traps found the same day, both worth not rediscovering:
  //   * ids 40/41/42 — called "undocumented" in this file until now — are CZK,
  //     JPY and CLP. They are in the account's dropdown and absent from the API
  //     doc's table entirely.
  //   * the disagreement runs the other way too: TRY, EGP, LBP, QAR, DZD, PEN
  //     and COP are in the doc's table but NOT in this account's dropdown, so
  //     adding one on the doc's authority alone would offer a donor a currency
  //     the account cannot charge. The doc describes the API's superset; the
  //     dropdown describes this amuta. Re-read the dropdown, not the PDF.
  PLN: {
    code: 'PLN', id: '14', countryId: '170', symbol: 'zł', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 2.4, min: 20,
    label: { he: 'זלוטי פולני', ar: 'زلوتي بولندي' },
  },
  CZK: {
    code: 'CZK', id: '40', countryId: '56', symbol: 'Kč', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 13, min: 100,
    label: { he: 'קורונה צ׳כית', ar: 'كرونة تشيكية' },
  },
  HUF: {
    code: 'HUF', id: '10', countryId: '97', symbol: 'Ft', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 200, min: 1500,
    label: { he: 'פורינט הונגרי', ar: 'فورنت مجري' },
  },
  JPY: {
    code: 'JPY', id: '41', countryId: '107', symbol: '¥', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 100, min: 800,
    label: { he: 'ין יפני', ar: 'ين ياباني' },
  },
  SGD: {
    code: 'SGD', id: '15', countryId: '188', symbol: 'S$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 0.8, min: 5,
    label: { he: 'דולר סינגפורי', ar: 'دولار سنغافوري' },
  },
  HKD: {
    code: 'HKD', id: '9', countryId: '96', symbol: 'HK$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 5, min: 40,
    label: { he: 'דולר הונג קונגי', ar: 'دولار هونغ كونغي' },
  },
  KRW: {
    code: 'KRW', id: '19', countryId: '113', symbol: '₩', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 900, min: 7000,
    label: { he: 'וון דרום קוריאני', ar: 'وون كوري جنوبي' },
  },
  BRL: {
    code: 'BRL', id: '34', countryId: '30', symbol: 'R$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 3.2, min: 25,
    label: { he: 'ריאל ברזילאי', ar: 'ريال برازيلي' },
  },
  MXN: {
    code: 'MXN', id: '37', countryId: '138', symbol: 'MX$', symbolFirst: true,
    recurring: false, addressRequired: false, feeFixed: 11, min: 80,
    label: { he: 'פזו מקסיקני', ar: 'بيزو مكسيكي' },
  },
};

export const DEFAULT_CURRENCY = 'ILS';

// Display order of the picker. ILS first — it's what almost every donor uses —
// then the five that carry the overwhelming majority of overseas giving, then
// the 2026-08-11 additions. Order is by expected volume, not alphabetical: a
// donor who needs one of the last fifteen will hunt for it either way, and a
// donor who needs dollars should not have to scroll past kroner to find them.
//
// Within the tail the grouping is regional (western Europe, then central
// Europe, then Asia-Pacific, then Latin America) because that is the order a
// donor scans a long list in — nobody reads 21 options, they look for their
// own part of the world and stop.
export const CURRENCY_ORDER = [
  'ILS', 'USD', 'EUR', 'GBP', 'CAD', 'AUD',
  'CHF', 'SEK', 'NOK', 'DKK', 'NZD', 'ZAR',
  'PLN', 'CZK', 'HUF',
  'JPY', 'SGD', 'HKD', 'KRW',
  'BRL', 'MXN',
];

// Unknown/absent codes fall back to shekels rather than throwing: a bad value
// out of a URL param or stale state must never break checkout.
export function currencyMeta(code) {
  return CURRENCIES[String(code || '').toUpperCase()] || CURRENCIES[DEFAULT_CURRENCY];
}

export function isForeign(code) {
  return currencyMeta(code).code !== DEFAULT_CURRENCY;
}

// Street + postcode are collected for EVERY foreign currency, not just the ones
// where IsraelGives insists (`addressRequired`). Their address inputs are
// hidden in a POSTed flow — the fields carry class `postHide` — so a donor can
// never fill one in themselves; whatever we don't send, nobody can. Collecting
// it uniformly also keeps one form shape for all foreign donors, and Gramsci
// has street/zip_code columns waiting for it.
export function needsAddress(code) {
  return isForeign(code);
}

// Whether IsraelGives' own `AdressIsMandatory()` covers this currency
// (CAD/GBP/EUR). Nothing insists on it any more: their POST-fields doc marks
// only fname/lname/email required, and in the POSTed iframe that rule only
// checks visible fields, which the address inputs never are. The English form
// asks for the address in every currency and requires it in none.
export function isAddressRequired(code) {
  return currencyMeta(code).addressRequired;
}

// "₪ 50" vs "$50": the shekel sits after the digits in Hebrew, the rest before.
export function formatAmount(amount, code) {
  const c = currencyMeta(code);
  const n = amount === '' || amount == null ? '' : amount;
  return c.symbolFirst ? `${c.symbol}${n}` : `${n} ${c.symbol}`;
}

// Preset amounts per currency and frequency.
//
// The shekel rows are the existing ones (data.js / the campaign's own Gramsci
// settings) and are supplied by the caller, so they stay editable where they
// always were. Only the foreign ladders live here — they're deliberately
// round numbers in each currency rather than converted shekel amounts, which
// would read as "$13.51".
//
// Every currency carries a `monthly` row, and every one of them is now
// reachable: the monthly tab and the membership page offer the full picker
// (see the DECIDED note at the top of this file). A currency added without a
// `monthly` row hands the amount grid `undefined` to map over, which is what
// the "both frequencies" test in igIframe.currency.test.js guards.
//
// The ladders are calibrated to be worth roughly what the dollar ladder is
// (~₪30 to ~₪1500), which is why they are not all the same digits: 100 krona
// and $10 are the same donation, 100 rand is a fifth of it.
const FOREIGN_PRESETS = {
  USD: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  EUR: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  GBP: { once: ['10', '20', '50', '100', '150', '250', '500'], monthly: ['5', '10', '15', '25', '50', '100'] },
  CAD: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  AUD: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  CHF: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  SEK: { once: ['100', '250', '500', '1000', '1800', '2500', '5000'], monthly: ['50', '100', '180', '250', '500', '1000'] },
  NOK: { once: ['100', '250', '500', '1000', '1800', '2500', '5000'], monthly: ['50', '100', '180', '250', '500', '1000'] },
  DKK: { once: ['70', '150', '350', '700', '1200', '2000', '3500'], monthly: ['35', '70', '125', '180', '350', '700'] },
  NZD: { once: ['20', '50', '100', '180', '300', '500', '1000'], monthly: ['10', '20', '35', '50', '100', '200'] },
  ZAR: { once: ['150', '400', '800', '1500', '3000', '5000', '10000'], monthly: ['80', '150', '300', '450', '900', '1800'] },
  // Built the same way as the rows above: pick the round unit closest to ₪30
  // in that currency, then walk it up 1 / 2.5 / 5 / 10 / 18 / 25 / 50. That is
  // why the digits differ so widely — 1,500 yen and 40 zloty are the same gift.
  PLN: { once: ['40', '100', '200', '400', '700', '1000', '2000'], monthly: ['20', '40', '70', '100', '200', '400'] },
  CZK: { once: ['200', '500', '1000', '2000', '3500', '5000', '10000'], monthly: ['100', '200', '350', '500', '1000', '2000'] },
  HUF: { once: ['3000', '8000', '15000', '30000', '55000', '80000', '150000'], monthly: ['1500', '3000', '5500', '8000', '15000', '30000'] },
  JPY: { once: ['1500', '4000', '8000', '15000', '25000', '40000', '80000'], monthly: ['800', '1500', '3000', '5000', '8000', '15000'] },
  SGD: { once: ['10', '25', '50', '100', '180', '250', '500'], monthly: ['5', '10', '18', '25', '50', '100'] },
  HKD: { once: ['100', '250', '500', '1000', '1800', '2500', '5000'], monthly: ['50', '100', '180', '250', '500', '1000'] },
  KRW: { once: ['15000', '30000', '70000', '150000', '250000', '350000', '700000'], monthly: ['7000', '15000', '25000', '35000', '70000', '150000'] },
  BRL: { once: ['50', '100', '250', '500', '900', '1500', '2500'], monthly: ['25', '50', '100', '150', '300', '500'] },
  MXN: { once: ['150', '400', '800', '1500', '2500', '4000', '8000'], monthly: ['80', '150', '300', '500', '900', '1800'] },
};

// `ilsAmounts` is whatever the page would have shown before this file existed.
export function presetAmounts(code, recurring, ilsAmounts) {
  const c = currencyMeta(code);
  if (c.code === DEFAULT_CURRENCY) return ilsAmounts;
  const row = FOREIGN_PRESETS[c.code];
  return (recurring ? row.monthly : row.once);
}

// IsraelGives' country list, restricted to the countries a foreign donor is
// realistically giving from. Values are their own country ids (the same list
// `countryid` is POSTed against; Israel is 104). Kept short on purpose — the
// full list is 263 entries and this is a dropdown a donor reads, not a census.
export const COUNTRIES = [
  { id: '223', en: 'United States' },
  { id: '222', en: 'United Kingdom' },
  { id: '38', en: 'Canada' },
  { id: '13', en: 'Australia' },
  { id: '73', en: 'France' },
  { id: '81', en: 'Germany' },
  { id: '150', en: 'Netherlands' },
  { id: '21', en: 'Belgium' },
  { id: '204', en: 'Switzerland' },
  { id: '195', en: 'Spain' },
  { id: '105', en: 'Italy' },
  { id: '103', en: 'Ireland' },
  // Homes of the currencies added 2026-08-11 — a donor giving in kroner needs
  // their own country in this list or they cannot complete the address step.
  { id: '203', en: 'Sweden' },
  { id: '160', en: 'Norway' },
  { id: '57', en: 'Denmark' },
  { id: '153', en: 'New Zealand' },
  { id: '193', en: 'South Africa' },
  // Homes of the second batch, same rule — a currency whose default country is
  // missing here makes the foreign form uncompletable in that currency, which
  // is what the "default country is one the donor can actually pick" test
  // guards. Ids read off the same #ddlcountry list.
  { id: '170', en: 'Poland' },
  { id: '56', en: 'Czech Republic' },
  { id: '97', en: 'Hungary' },
  { id: '107', en: 'Japan' },
  { id: '188', en: 'Singapore' },
  { id: '96', en: 'Hong Kong' },
  { id: '113', en: 'South Korea' },
  { id: '30', en: 'Brazil' },
  { id: '138', en: 'Mexico' },
  { id: '104', en: 'Israel' },
];

// Gramsci's donor record stores a country NAME, while IsraelGives wants its own
// numeric id — so the form holds the id and this resolves the name to record.
export function countryName(id) {
  const found = COUNTRIES.find((c) => c.id === String(id));
  return found ? found.en : null;
}
